import React, { Fragment } from "react";
import { Check } from "lucide-react";
import { cn } from "../../../lib/cn";

/**
 * Stepper — barra de pasos para wizards con líneas conectoras horizontales.
 *
 * Estética estilo Soporte Mockup:
 *   - Círculos numerados (activo en navy, futuros en gris claro, completados en navy con check)
 *   - Líneas horizontales que conectan los círculos (no flechas)
 *   - Texto a la derecha del círculo
 *   - Las líneas entre pasos completados se pintan en navy
 *
 * Uso:
 *   <Stepper steps={steps} activeId="info" />
 *   <Stepper steps={steps} activeId="fechas" onStepClick={(id) => irPaso(id)} />
 */

export function Stepper({ steps = [], activeId, onStepClick, className }) {
  const activeIdx = steps.findIndex((s) => s.id === activeId);

  return (
    <div className={cn("flex items-center w-full", className)}>
      {steps.map((step, idx) => {
        const isActive = idx === activeIdx;
        const isCompleted = idx < activeIdx;
        const isClickable = !!onStepClick && (isCompleted || isActive);

        return (
          <Fragment key={step.id}>
            <StepCircleWithLabel
              step={step}
              index={idx}
              active={isActive}
              completed={isCompleted}
              clickable={isClickable}
              onClick={isClickable ? () => onStepClick(step.id) : undefined}
            />

            {idx < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-px mx-3 transition-colors",
                  isCompleted ? "bg-primary" : "bg-slate-200"
                )}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function StepCircleWithLabel({ step, index, active, completed, clickable, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={cn(
        "flex items-center gap-2.5 shrink-0",
        clickable && "cursor-pointer",
        !clickable && "cursor-default"
      )}
    >
      <span
        className={cn(
          "inline-flex items-center justify-center w-7 h-7 rounded-full text-[12px] font-semibold transition-colors",
          active && "bg-primary text-white",
          completed && "bg-primary text-white",
          !active && !completed && "bg-slate-100 text-slate-400"
        )}
      >
        {completed ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : index + 1}
      </span>
      <span
        className={cn(
          "text-sm transition-colors whitespace-nowrap",
          active && "text-slate-900 font-medium",
          completed && "text-slate-700",
          !active && !completed && "text-slate-400"
        )}
      >
        {step.label}
      </span>
    </button>
  );
}
